import React, { useState, useEffect } from "react";
import ChecklistItem from "./ChecklistItem";
import { ChoiceGroup } from "./ChoiceCard";
import { ELIGIBILITY_PEER_DIM_STYLE } from "./checkerFieldStyles";

// Top 10 most common Singapore government grants for SMEs
const top10Grants = [
  { code: "PSG", label: "Productivity Solutions Grant (PSG)" },
  { code: "EDG", label: "Enterprise Development Grant (EDG)" },
  { code: "MRA", label: "Market Readiness Assistance (MRA)" },
  { code: "SFEC", label: "SkillsFuture Enterprise Credit (SFEC)" },
  { code: "SFEG", label: "Skills Framework Enhancement Grant (SFEG)" },
  { code: "EIS", label: "Enterprise Innovation Scheme (EIS)" },
  { code: "DIGITAL", label: "SMEs Go Digital" },
  { code: "FINTECH", label: "MAS FinTech Grant" },
  { code: "STARTUP_FOUNDER", label: "Startup SG Founder" },
  { code: "EEG", label: "Energy Efficiency Grant (EEG)" }
];

export default function GrantHistoryStep({ formData, updateFormData, errors = {} }) {
  // Initialize state from formData with new structure
  const [selectedGrants, setSelectedGrants] = useState(formData.previous_grants || []);
  const [hasOther, setHasOther] = useState(formData.previous_grants_other || false);
  const [hasNone, setHasNone] = useState(formData.previous_grants_none || false);
  const dimByNone = hasNone;

  // Sync state to formData whenever it changes
  useEffect(() => {
    updateFormData({
      previous_grants: selectedGrants,
      previous_grants_other: hasOther,
      previous_grants_none: hasNone,
      // Maintain backward compatibility with old field names
      prior_apps: hasNone ? ["None previously received"] : [...selectedGrants, ...(hasOther ? ["Others"] : [])]
    });
  }, [selectedGrants, hasOther, hasNone, updateFormData]);

  const handleGrantChange = (grantCode, checked) => {
    if (checked) {
      setSelectedGrants([...selectedGrants, grantCode]);
      // Clear "None" when selecting any grant
      if (hasNone) setHasNone(false);
    } else {
      setSelectedGrants(selectedGrants.filter(g => g !== grantCode));
    }
  };

  const handleOthersChange = (checked) => {
    setHasOther(checked);
    // Clear "None" when selecting Others
    if (checked && hasNone) setHasNone(false);
  };

  const handleNoneChange = (checked) => {
    setHasNone(checked);
    // Clear all other selections when "None" is checked
    if (checked) {
      setSelectedGrants([]);
      setHasOther(false);
    }
  };

  return (
    <div id="step-history" className="space-y-10">
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold mb-3" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Grant History</h2>
        <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4B5563' }}>
          Previous grant applications help us provide better recommendations
        </p>
      </div>

      {/* Form Fields */}
      <div className="space-y-8">
        {/* Grant History Section */}
        <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
          <ChoiceGroup
            name="grant_history"
            role="group"
            legend="Previous Grant Experience"
            required
            hint="Select any grants you've previously received or applied for, or choose “None previously received”."
            error={errors.grant_history}
            className="space-y-4"
            optionsClassName="space-y-0"
          >
            {/* Top 10 Common Grants */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
              {top10Grants.map(grant => (
                <div
                  key={grant.code}
                  style={dimByNone ? ELIGIBILITY_PEER_DIM_STYLE : undefined}
                >
                  <ChecklistItem
                    id={`grant-${grant.code}`}
                    label={grant.label}
                    checked={selectedGrants.includes(grant.code)}
                    onCheckedChange={(checked) => handleGrantChange(grant.code, checked)}
                    disabled={false}
                    compact={true}
                  />
                </div>
              ))}
            </div>

            {/* Others & None checkboxes */}
            <div className="mt-6 space-y-0 border-t border-white/10 pt-4">
              <div style={dimByNone ? ELIGIBILITY_PEER_DIM_STYLE : undefined}>
                <ChecklistItem
                  id="grant-others"
                  label="Others (grants not listed above)"
                  checked={hasOther}
                  onCheckedChange={handleOthersChange}
                  disabled={false}
                  compact={false}
                />
              </div>

              <ChecklistItem
                id="grant-none"
                label="None previously received"
                checked={hasNone}
                onCheckedChange={handleNoneChange}
                compact={false}
              />
            </div>
          </ChoiceGroup>

          <div className="space-y-4">
            <div
              className="checker-info-box mt-4"
            >
              <div className="text-sm font-semibold mb-2" style={{ color: "#1F2937" }}>
                Why we ask:
              </div>
              <div className="text-sm leading-relaxed">
                <ul className="space-y-1">
                  <li>• Previous grant experience can strengthen future applications</li>
                  <li>• Some grants have waiting periods or limits on repeat applications</li>
                  <li>• We can recommend complementary grants based on your history</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
